import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Badge,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
  GlobalStyles,
  Paper,
  Grid,
  alpha
} from '@mui/material';
import {
  FilterList,
  Search as SearchIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Done as DoneIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

import SiteVisitList from './SiteVisitList';
import SiteVisitFilter from './SiteVisitFilter';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import Pagination from '../../components/common/Pagination';
import CreateNewTask from '../../components/common/CreateNewTask';
import RescheduleSiteVisit from './RescheduleVisit';
import { useSiteVisit } from '../../contexts/SiteVisitContext';
import CreateNewSiteVisit from './CreateNewSiteVisit'
import SiteVisitSidebar from './Sidebar'; 
import { useAuth } from '../../contexts/AuthContext';

import dayjs from 'dayjs';
import { 
  fetchAgentSelect,
  fetchLeadSelect,
  fetchProjectSelect,
  fetchSiteVisitStatusOptions,
  fetchTransportationTypeOptions,
  fetchVisitTypeOptions,
  fetchConfigurationSelect,
  fetchUnitSelect

} from '../../services/common';

// Initialize dayjs plugins
dayjs.extend(require('dayjs/plugin/utc'));
dayjs.extend(require('dayjs/plugin/timezone'));

const defaultDateRange = {
  filter: { id: 'All', label: 'All' },
  currentStart: null,
  currentEnd: null,
  compareStart: null,
  compareEnd: null,
  all_results: true,
};

const SiteVisitBase = () => {
  const location = useLocation();
  const isDetailsRoute = /^\/site-visits\/\d+$/.test(location.pathname);
  const { user } = useAuth();
  
  // Reference data
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [transportationOptions, setTransportationOptions] = useState([]);
  const [visitTypeOptions, setVisitTypeOptions] = useState([]);
  const [leadInterestLevelOptions, setLeadInterestLevelOptions] = useState([]); 
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedSiteVisitId, setSelectedSiteVisitId] = useState(null);
  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedSidebarStatus, setSelectedSidebarStatus] = useState('all');

  // Get context data
  const {
    siteVisits,
    fetchSiteVisits,
    pagination,
    appliedFilters,
    setAppliedFilters,
    completeSiteVisit,
    cancelSiteVisit,
    createSiteVisit,
    fetchSiteVisitStatusCounts,
    statusCounts,
    setStatusCounts,
    isLoading: contextIsLoading
  } = useSiteVisit();

  // State management
  const [dateRangeState, setDateRangeState] = useState(defaultDateRange);
  
  // State for filters
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterOpen(true);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setFilterOpen(false);
  };
  
  // Pagination state - using 1-based page numbers
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Sorting state
  const [sortField, setSortField] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Search state
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [isFilterUpdateInProgress, setIsFilterUpdateInProgress] = useState(false);
  const [urlParamsApplied, setUrlParamsApplied] = useState(false);

  // Helper function to format numbers like Google (1,234 results)
  const formatResultsCount = (count) => {
    return count.toLocaleString();
  };

  useEffect(() => {
    fetchReferenceData();
  }, []);

  // Handle URL query parameters (from agent report page)
  useEffect(() => {
    console.log('SiteVisits component - checking for URL query parameters');
    
    try {
      const searchParams = new URLSearchParams(location.search);
      
      // Check if we have URL parameters
      if (searchParams.toString()) {
        console.log('🎯 Found URL query parameters:', searchParams.toString());
        
        const filters = {};
        let hasDateRange = false;
        let sidebarStatus = 'all'; // Default to 'all'
        
        // Parse agents filter
        if (searchParams.has('agents')) {
          const agentIds = searchParams.get('agents').split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
          if (agentIds.length > 0) {
            filters.agents = agentIds;
          }
        }
        
        // Parse status filter and determine sidebar status
        if (searchParams.has('status')) {
          const statusValue = searchParams.get('status');
          filters.status = statusValue;
          
          // Map status to sidebar status
          if (statusValue === 'scheduled') {
            sidebarStatus = 'scheduled';
          } else if (statusValue === 'completed') {
            sidebarStatus = 'completed';
          } else if (statusValue === 'cancelled') {
            sidebarStatus = 'cancelled';
          }
        }
        
        // Parse tags filter
        if (searchParams.has('tags')) {
          const tags = searchParams.get('tags').split(',').map(tag => tag.trim());
          filters.tags = tags;
          
          // If tags include 'visit confirmed', set sidebar to confirmed
          if (tags.some(tag => tag.toLowerCase().includes('visit confirmed'))) {
            sidebarStatus = 'confirmed';
          }
        }
        
        // Parse date range and date filter label
        const startDate = searchParams.get('start_date');
        const endDate = searchParams.get('end_date');
        const dateFilterLabel = searchParams.get('date_filter'); // Get the date filter label from URL
        
        if (startDate && endDate) {
          const start = dayjs(startDate);
          const end = dayjs(endDate);
          
          if (start.isValid() && end.isValid()) {
            // Use the date filter label from URL if available, otherwise default to 'Custom'
            const filterLabel = dateFilterLabel || 'Custom';
            
            // Determine if this is "All" results based on the filter label
            const isAllResults = filterLabel === 'All';
            
            const newDateRangeState = {
              filter: { id: filterLabel, label: filterLabel },
              currentStart: isAllResults ? null : start,
              currentEnd: isAllResults ? null : end,
              compareStart: null,
              compareEnd: null,
              all_results: isAllResults,
            };
            
            setDateRangeState(newDateRangeState);
            hasDateRange = true;
            console.log('✅ Applied date range from URL:', { startDate, endDate, filterLabel });
          }
        }
        
        // Apply filters if we have any
        if (Object.keys(filters).length > 0) {
          setAppliedFilters(filters);
          console.log('✅ Applied filters from URL:', filters);
        }
        
        // Set the sidebar status
        setSelectedSidebarStatus(sidebarStatus);
        console.log('✅ Set sidebar status from URL:', sidebarStatus);
        
        // Mark URL params as applied
        if (hasDateRange || Object.keys(filters).length > 0) {
          setUrlParamsApplied(true);
        }
      } else {
        // No URL params, mark as applied (nothing to apply)
        setUrlParamsApplied(true);
      }
    } catch (error) {
      console.error('Error handling URL query parameters:', error);
      setUrlParamsApplied(true);
    }
  }, [location.search, setAppliedFilters]);

  // Handle navigation state from dashboard (localStorage, no query params)
  useEffect(() => {
    console.log('SiteVisits component - checking for dashboard navigation data');
    
    try {
      // Check for dashboard navigation state in localStorage
      const storedData = localStorage.getItem('lead_filters');
      if (storedData) {
        const dashboardData = JSON.parse(storedData);
        console.log('SiteVisits component - dashboard data:', dashboardData);
        
        // Check if data is fresh (within last 5 minutes)
        const isFresh = Date.now() - dashboardData.timestamp < 5 * 60 * 1000;
        
        if (isFresh && dashboardData.policy) {
          console.log('🎯 Applying exact policy payload from localStorage to site visits:', dashboardData.policy);
          
          const policy = dashboardData.policy;
          
          // Extract filters for site visits from the exact policy
          const { status, ...otherFilters } = policy.filters;
          
          // Set applied filters for site visits
          const siteVisitFilters = {};
          if (status) siteVisitFilters.status = status;
          
          console.log('✅ Setting site visit filters from policy:', siteVisitFilters);
          setAppliedFilters(prev => ({ ...prev, ...siteVisitFilters }));
          
          // Apply date range from the exact policy
          if (policy.date_range) {
            const { start_date, end_date } = policy.date_range;
            const start = dayjs(start_date);
            const end = dayjs(end_date);
            
            const newDateRangeState = {
              filter: { id: 'Custom', label: 'Custom' },
              currentStart: start,
              currentEnd: end,
              compareStart: null,
              compareEnd: null,
              all_results: policy.all_results || false,
            };
            
            setDateRangeState(newDateRangeState);
          }
          
          // Clear the localStorage data after applying
          localStorage.removeItem('lead_filters');
          console.log('✅ Cleared dashboard navigation data from localStorage');
        } else if (!isFresh) {
          // Clear stale data
          localStorage.removeItem('lead_filters');
          console.log('Cleared stale dashboard navigation data from localStorage');
        }
      }
    } catch (error) {
      console.error('Error handling dashboard navigation data:', error);
      // Clear corrupted data
      localStorage.removeItem('lead_filters');
    }
  }, [setAppliedFilters]);

  // Unified function to build API parameters
  const buildApiParams = useCallback((searchTerm = null, customFilters = null, customDateRange = null, customPage = null, customPageSize = null) => {
    const params = {
      // Pagination
      page: customPage || page,
      page_size: customPageSize || rowsPerPage,
      // Sorting
      sort_field: sortField,
      sort_order: sortOrder
    };

    // Add search if provided
    if (searchTerm) {
      params.search = searchTerm;
    }

    // Add filters
    const filters = customFilters || appliedFilters;
    if (filters && typeof filters === 'object') {
      Object.keys(filters).forEach(key => {
        // Handle boolean values and other valid values
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          params[key] = filters[key];
        } else if (typeof filters[key] === 'boolean') {
          // Always include boolean values (true/false)
          params[key] = filters[key];
        }
      });
    }

    // Handle all_results flag from date range filter
    const dateRange = customDateRange || dateRangeState;
    if (dateRange && dateRange.all_results !== undefined) {
      params.all_results = dateRange.all_results;
    } else if (dateRange && dateRange.filter?.id === 'All') {
      // Fallback: explicitly indicate "All" filter to backend
      params.all_results = true;
    }
    
    // Add date range if provided and not "All" results
    if (dateRange && dateRange.currentStart && dateRange.currentEnd && !params.all_results) {
      params.date_range = {
        start_date: dateRange.currentStart.format('YYYY-MM-DD'),
        end_date: dateRange.currentEnd.format('YYYY-MM-DD')
      };
    }

    return params;
  }, [page, rowsPerPage, appliedFilters, dateRangeState, sortField, sortOrder]);

  // Unified fetch function
  const fetchSiteVisitsData = useCallback((searchTerm = null, customFilters = null, customDateRange = null, customPage = null, customPageSize = null, forceRefresh = false) => {
    const params = buildApiParams(searchTerm, customFilters, customDateRange, customPage, customPageSize);
    return fetchSiteVisits(params, customDateRange || dateRangeState, searchTerm, forceRefresh);
  }, [buildApiParams, fetchSiteVisits, dateRangeState]);

  // Handle column sorting
  const handleSort = useCallback((field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    setPage(1); // Reset to first page when sorting changes
    
    // Build params with new sort values
    const params = buildApiParams(searchKeyword, appliedFilters, dateRangeState, 1, rowsPerPage);
    params.sort_field = field;
    params.sort_order = newOrder;
    fetchSiteVisits(params, dateRangeState, searchKeyword, true);
  }, [sortField, sortOrder, searchKeyword, appliedFilters, dateRangeState, rowsPerPage, buildApiParams, fetchSiteVisits]);

  // Handle date range change from DateRangeFilter
  const handleDateRangeChange = useCallback((rangeObj) => {
    const oldFilterId = dateRangeState?.filter?.id;
    const newFilterId = rangeObj?.filter?.id;
  
    if (
      oldFilterId !== newFilterId ||
      dateRangeState.currentStart?.format('YYYY-MM-DD') !== rangeObj.currentStart?.format('YYYY-MM-DD') ||
      dateRangeState.currentEnd?.format('YYYY-MM-DD') !== rangeObj.currentEnd?.format('YYYY-MM-DD')
    ) {
      setDateRangeState(rangeObj);
    }
  }, [dateRangeState]);

  // Search submit handler for Enter key
  const handleSearchSubmit = (searchTerm) => {
    if (searchTerm.trim().length >= 3) {
      setIsSearchActive(true);
      setIsSearching(true);
      setIsFilterUpdateInProgress(true);
      setPage(1);
      setSearchKeyword(searchTerm);
      fetchSiteVisitsData(searchTerm, null, null, 1, null).finally(() => {
        setIsSearching(false);
        setIsFilterUpdateInProgress(false);
      });
    }
  };

  // Pagination handlers - simplified like Leads.js
  const handleChangePage = (newPage) => {
    // Ensure page is within valid range [1, total_pages]
    const maxPage = Math.ceil((pagination?.total_count || 0) / rowsPerPage) || 1;
    const validPage = Math.max(1, Math.min(newPage, maxPage));
    
    // Only proceed if the page actually changes and is valid
    if (validPage !== page && validPage >= 1 && validPage <= maxPage) {
      setPage(validPage);
      
      // Use unified fetch function - directly fetch without triggering other effects
      const searchParam = isSearchActive ? searchKeyword : null;
      fetchSiteVisitsData(searchParam, null, null, validPage, null);
    }
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to page 1 (1-based)
    
    // Use unified fetch function
    const searchParam = isSearchActive ? searchKeyword : null;
    fetchSiteVisitsData(searchParam, null, null, 1, newRowsPerPage);
  };

  // Calculate active filters and their display names
  const activeFilters = useMemo(() => {
    const entries = Object.entries(appliedFilters || {});
    const filters = [];
    
    entries.forEach(([key, value]) => {
      // For arrays, check if they have content
      if (Array.isArray(value) && value.length > 0) {
        const filterNames = {
          'agents': 'Agent',
          'agent_id': 'Agent',
          'agent_ids': 'Agent',
          'projects': 'Project',
          'project_id': 'Project',
          'project_ids': 'Project',
          'statuses': 'Status',
          'status': 'Status',
          'visit_types': 'Visit Type',
          'visit_type': 'Visit Type',
          'transportation_types': 'Transportation',
          'transportation_type': 'Transportation',
          'tags': 'Tags'
        };
        filters.push(filterNames[key] || key);
      }
      // For other values, check if they're truthy
      else if (value !== null && value !== undefined && value !== '') {
        const filterNames = {
          'agents': 'Agent',
          'agent_id': 'Agent',
          'agent_ids': 'Agent',
          'projects': 'Project',
          'project_id': 'Project',
          'project_ids': 'Project',
          'statuses': 'Status',
          'status': 'Status',
          'visit_types': 'Visit Type',
          'visit_type': 'Visit Type',
          'transportation_types': 'Transportation',
          'transportation_type': 'Transportation',
          'tags': 'Tags'
        };
        filters.push(filterNames[key] || key);
      }
    });
    
    return filters;
  }, [appliedFilters]);

  const activeFilterCount = activeFilters.length;

  // Check if any filters are active for Reset button
  const hasAnyFilters = useMemo(() => {
    const entries = Object.entries(appliedFilters || {});
    return entries.some(([key, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== undefined && value !== '';
    });
  }, [appliedFilters]);

  // Function to clear applied filters and fetch all site visits
  const handleResetFilters = useCallback(() => {
    setAppliedFilters({}); // Clear filters in context
    setSearchKeyword(''); // Clear search
    setIsSearchActive(false); // Reset search state
    setSelectedSidebarStatus('all');
    setPage(1);
    
    // Use the unified fetch function with reset parameters
    fetchSiteVisitsData(null, {}, dateRangeState, 1, rowsPerPage, true);
    
    // Optionally close the filter popover if it's open
    setFilterAnchorEl(null);
    setFilterOpen(false);
  }, [setAppliedFilters, dateRangeState, fetchSiteVisitsData, rowsPerPage]);

  // Search effect - only handle search reset when keyword is cleared
  useEffect(() => {
    // Only handle search reset when keyword is cleared
    if (searchKeyword.length === 0 && isSearchActive) {
      setIsSearchActive(false);
      setIsSearching(false);
      setPage(1); // Reset to page 1
      fetchSiteVisitsData(null, null, null, 1, null, true);
    }
  }, [searchKeyword]);

  // Main data fetching effect - handles filters and date range changes (NOT pagination)
  useEffect(() => {
    // Skip if URL params haven't been applied yet
    if (!urlParamsApplied) {
      return;
    }
    
    // Skip if this is just a search state change - search has its own effect
    if (isSearchActive || isFilterUpdateInProgress) {
      return;
    }
    
    // Fetch the site visits if we have date range state
    if (!isDetailsRoute) {
      // Reset to page 1 when filters change
      setPage(1);
      // Use current appliedFilters
      fetchSiteVisitsData(null, appliedFilters, dateRangeState, 1, null);
    }
  }, [appliedFilters, dateRangeState, isDetailsRoute, isSearchActive, isFilterUpdateInProgress, urlParamsApplied]);

  // Handle site visit updated
  const handleSiteVisitUpdated = (data) => {
    // Refresh the site visits data after a site visit is updated
    console.log('Site visit updated, refreshing data:', data);
    fetchSiteVisitsData(null, appliedFilters, dateRangeState, 1, rowsPerPage, true);
  };

  const fetchReferenceData = async () => {
    const [
      agents,
      leads,
      projects,
      statusOptions,
      transportationOptions,
      visitTypeOptions,
    ] = await Promise.all([
      fetchAgentSelect(),
      fetchLeadSelect(),
      fetchProjectSelect(),
      fetchSiteVisitStatusOptions(),
      fetchTransportationTypeOptions(),
      fetchVisitTypeOptions(),
    ]);

    setAgents(agents);
    setLeads(leads);
    setProjects(projects);
    setStatusOptions(statusOptions);
    setTransportationOptions(transportationOptions);
    setVisitTypeOptions(visitTypeOptions);
  };

  const handleRescheduleSiteVisit = (visit) => {
    setRescheduleModalOpen(true);
    setSelectedSiteVisit(visit);
    setSelectedSiteVisitId(visit.id);
  };

  const handleCompleteVisit = (visitId, notes) => {
    setSelectedSiteVisitId(visitId);
    completeSiteVisit(visitId, notes);
  };

  const handleCancelVisit = (visitId) => {
    setSelectedSiteVisitId(visitId);
    // You can implement a cancel modal here if needed
    // For now, we'll use the context's cancelSiteVisit function
    // This would need to be added to the SiteVisitContext
    console.log('Cancelling site visit:', visitId);
  };

  const handleCreateSiteVisit = async (data) => {
    console.log('Creating site visit:', data);
    try {
      await createSiteVisit(data);
      // If successful, the modal will be closed by the CreateNewSiteVisit component
    } catch (error) {
      console.error('Error in handleCreateSiteVisit:', error);
      // Re-throw the error so it can be handled by the CreateNewSiteVisit component
      throw error;
    }
  };


  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };


  // Fetch status counts
  useEffect(() => {
    const fetchStatusCounts = async () => {
      try {
        const counts = await fetchSiteVisitStatusCounts(dateRangeState);
        setStatusCounts(counts);
      } catch (error) {
        console.error('Error fetching status counts:', error);
        // Don't show error to user for status counts, just log it
        setStatusCounts({
          all: { count: 0 },
          my_visits: { count: 0 },
          pending: { count: 0 },
          scheduled: { count: 0 },
          completed: { count: 0 },
          cancelled: { count: 0 },
          expired: { count: 0 },
        });
      }
    };
    fetchStatusCounts();
  }, [dateRangeState]);

  return (
    <>
      <GlobalStyles
        styles={{
          '@keyframes pulse': {
            '0%': {
              opacity: 1,
            },
            '50%': {
              opacity: 0.5,
            },
            '100%': {
              opacity: 1,
            },
          },
        }}
      />
      <Box sx={{ display: 'flex', height: '100vh' }}>
        {/* Sidebar */}
        {/* <SiteVisitSidebar
          width={sidebarCollapsed ? 10 : 200}
          collapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          dateRangeState={dateRangeState}
          selectedStatus={selectedSidebarStatus}
          setAppliedFilters={setAppliedFilters}
          user={user}
          onStatusChange={setSelectedSidebarStatus}
        /> */}
        
        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Top Bar */}
          <Box
            sx={{
              p: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Box>
                <DateRangeFilter 
                  onChange={handleDateRangeChange} 
                  initialDateRange={dateRangeState}
                />
              </Box>
            </Stack>

            {/* Filter, Search, and Pagination Section */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mt: 1.5,
              mb: 1
            }}>
              {/* Filter and Search */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
                {/* Filter Button */}
                <Tooltip 
                  title={activeFilterCount > 0 ? `Applied filters: ${activeFilters.join(', ')}` : 'Click to apply filters'}
                  placement="bottom"
                >
                  <Button
                    size="medium"
                    startIcon={<FilterList />}
                    variant={activeFilterCount > 0 ? "contained" : "outlined"}
                    onClick={handleFilterClick}
                    sx={{
                      minWidth: activeFilterCount > 0 ? '120px' : '100px',
                      height: '36px',
                      borderRadius: 2,
                      fontWeight: 500,
                      position: 'relative',
                      ...(activeFilterCount > 0 && {
                        bgcolor: 'primary.light',
                        color: 'primary.black',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.main',
                          color: 'white',
                        }
                      })
                    }}
                  >
                    {activeFilterCount > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Badge 
                          badgeContent={activeFilterCount} 
                          color="error" 
                          sx={{ 
                            '& .MuiBadge-badge': { 
                              fontSize: '0.7rem',
                              height: '18px',
                              minWidth: '18px',
                              borderRadius: '9px'
                            } 
                          }}
                        >
                          <span>Filters</span>
                        </Badge>
                      </Box>
                    ) : (
                      'Filter'
                    )}
                  </Button>
                </Tooltip>

                {/* Search Bar */}
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    size="small"
                    placeholder="Search site visits by lead name, agent name, project name"
                    sx={{ 
                      minWidth: '500px',
                      '& .MuiOutlinedInput-root': {
                        height: '36px'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: isSearching ? 'primary.info' : 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                          borderWidth: '2px'
                        }
                      }
                    }}
                    value={searchKeyword}
                    onChange={(e) => {
                      setSearchKeyword(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchKeyword && typeof searchKeyword === 'string' && searchKeyword.trim()) {
                        handleSearchSubmit(searchKeyword.trim());
                      }
                    }}
                  />
                  
                  {/* Search Status Messages */}
                  <Box sx={{ mt: 0.5, minHeight: '16px' }}>
                    {isSearching && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary', 
                          fontSize: '0.75rem',
                          fontStyle: 'italic'
                        }}
                      >
                        Searching...
                      </Typography>
                    )}
                    
                    {searchKeyword.length > 0 && !isSearchActive && !isSearching && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary', 
                          fontSize: '0.75rem'
                        }}
                      >
                        {searchKeyword.length < 3 ? 'Type at least 3 characters and press Enter to search' : 'Press Enter to search'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Pagination */}
              <Pagination
                totalCount={pagination?.total_count || 0}
                currentPage={page}
                pageSize={rowsPerPage}
                totalPages={Math.ceil((pagination?.total_count || 0) / rowsPerPage)}
                hasNext={page < Math.ceil((pagination?.total_count || 0) / rowsPerPage)}
                hasPrevious={page > 1}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsPerPage}
                formatResultsCount={formatResultsCount}
              />
            </Box>

            {/* Status Cards - Horizontal Layout */}
            <Box 
              sx={{ 
                mt: 1.5, 
                mb: 0, 
                pt: { md: '2px' },
                width: '100%',
                display: { xs: 'grid', sm: 'grid', md: 'flex' },
                gridTemplateColumns: { 
                  xs: '1fr', 
                  sm: 'repeat(2, 1fr)'
                },
                gap: { xs: 1, sm: 1 },
                flexWrap: { md: 'nowrap' },
                overflow: { xs: 'hidden', sm: 'hidden', md: 'visible' },
              }}
            >
              {[
                { id: 'all', label: 'All Visit', color: '#0070F3', icon: <EventIcon fontSize="small" />, filters: {} },
                { id: 'my_visits', label: 'My Visits', color: '#8B5CF6', icon: <PersonIcon fontSize="small" />, filters: user?.id ? { agents: [user.id] } : {} },
                { id: 'scheduled', label: 'Scheduled', color: '#0891B2', icon: <ScheduleIcon fontSize="small" />, filters: { status: 'scheduled' } },
                { id: 'confirmed', label: 'Confirmed', color: '#10B981', icon: <CheckCircleIcon fontSize="small" />, filters: { tags: ['visit confirmed'] } },
                { id: 'completed', label: 'Completed', color: '#059669', icon: <DoneIcon fontSize="small" />, filters: { status: 'completed' } },
                { id: 'cancelled', label: 'Cancelled', color: '#EF4444', icon: <CancelIcon fontSize="small" />, filters: { status: 'cancelled' } }
              ].map((statusConfig, index) => {
                const statusData = statusCounts?.[statusConfig.id];
                const count = Number(statusData?.count ?? 0);
                const isSelected = selectedSidebarStatus === statusConfig.id;
                const cardColor = statusConfig.color;

                return (
                  <Box 
                    key={statusConfig.id} 
                    sx={{ 
                      minWidth: 0, 
                      overflow: { xs: 'hidden', sm: 'hidden', md: 'visible' },
                      flex: { xs: 'none', sm: 'none', md: 1 },
                      flexBasis: { md: 0 },
                      marginRight: { md: index < 5 ? '3px' : 0 },
                      width: { xs: '100%', sm: 'auto' },
                      pt: { md: '2px' },
                      pb: { md: '2px' }
                    }}
                  >
                    <Paper
                      elevation={0}
                      onClick={() => {
                        setSelectedSidebarStatus(statusConfig.id);
                        if (statusConfig.id === 'my_visits' && user?.id) {
                          setAppliedFilters({ agents: [user.id] });
                        } else if (statusConfig.filters && Object.keys(statusConfig.filters).length > 0) {
                          setAppliedFilters(statusConfig.filters);
                        } else {
                          setAppliedFilters({});
                        }
                      }}
                      sx={{
                        py: 1,
                        px: { xs: 1.5, md: 0.75 },
                        borderRadius: 1.5,
                        border: isSelected 
                          ? `2px solid ${cardColor}` 
                          : `1px solid ${alpha(cardColor, 0.15)}`,
                        background: isSelected 
                          ? `linear-gradient(135deg, ${alpha(cardColor, 0.08)} 0%, ${alpha(cardColor, 0.04)} 100%)` 
                          : 'background.paper',
                        boxShadow: isSelected 
                          ? `0 3px 12px ${alpha(cardColor, 0.2)}, 0 1px 3px ${alpha(cardColor, 0.1)}` 
                          : `0 1px 3px ${alpha('#000', 0.08)}`,
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        height: '100%',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        flexShrink: 1,
                        '&:hover': {
                          boxShadow: `0 4px 16px ${alpha(cardColor, 0.25)}, 0 2px 4px ${alpha(cardColor, 0.15)}`,
                          transform: 'translateY(-2px)',
                          border: `2px solid ${alpha(cardColor, 0.5)}`,
                          background: `linear-gradient(135deg, ${alpha(cardColor, 0.1)} 0%, ${alpha(cardColor, 0.05)} 100%)`,
                        },
                        '&:active': {
                          transform: 'translateY(0px)',
                        },
                        ...(isSelected && {
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 3,
                            backgroundColor: cardColor,
                            borderRadius: '0 2px 2px 0',
                          },
                        }),
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.25,
                            bgcolor: alpha(cardColor, 0.12),
                            color: cardColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'all 0.25s ease',
                            ...(isSelected && {
                              bgcolor: alpha(cardColor, 0.18),
                            }),
                          }}
                        >
                          {statusConfig.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.625rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                              display: 'block',
                              mb: 0.25,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {statusConfig.label}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: isSelected ? cardColor : 'text.primary',
                              fontSize: '1.125rem',
                              lineHeight: 1,
                              transition: 'color 0.25s ease',
                            }}
                          >
                            {count.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Action Buttons Above Table */}
          <Box
            sx={{
              p: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              {/* Results Count and Reset Filter Button */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  {isSearching ? (
                    'Searching...'
                  ) : isSearchActive ? (
                    <>
                      {formatResultsCount(pagination?.total_count || 0)} search result{(pagination?.total_count || 0) !== 1 ? 's' : ''} found
                    </>
                  ) : (
                    <>
                      {formatResultsCount(pagination?.total_count || 0)} site visit{(pagination?.total_count || 0) !== 1 ? 's' : ''} found
                    </>
                  )}
                </Typography>
                
                {/* Reset Filter Button - Only show when filters are applied */}
                {hasAnyFilters && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleResetFilters}
                    sx={{
                      height: '24px',
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderRadius: 1,
                      backgroundColor: 'error.light',
                      color: 'white',
                      fontWeight: 'bold',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: 'white',
                      }
                    }}
                  >
                    Reset Filters
                  </Button>
                )}
              </Box>
            </Stack>
          </Box>

          {/* Site Visits List */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {contextIsLoading && siteVisits.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <Typography>Loading site visits...</Typography>
              </Box>
            ) : (
              <SiteVisitList
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort} 
                visits={siteVisits}
                isLoading={contextIsLoading}
                pagination={pagination}
                page={page - 1} // Convert to 0-based index for table
                rowsPerPage={rowsPerPage}
                searchKeyword={searchKeyword}
                isSearchActive={isSearchActive}
                onSiteVisitUpdated={handleSiteVisitUpdated}
                leadInterestLevelOptions={leadInterestLevelOptions}
                handleReschedule={handleRescheduleSiteVisit}
                handleCompleteVisit={handleCompleteVisit}
                handleCancel={handleCancelVisit}
              />
            )}
          </Box>
        </Box>

        {/* Site Visit Filter */}
        <SiteVisitFilter
          open={filterOpen}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          agents={agents}
          projects={projects}
          statusOptions={statusOptions}
          transportationOptions={transportationOptions}
          visitTypeOptions={visitTypeOptions}
          leadInterestLevelOptions={leadInterestLevelOptions}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          fetchSiteVisits={fetchSiteVisits}
          siteVisits={siteVisits}
          dateRangeState={dateRangeState}
        />

        <CreateNewSiteVisit
          open={formModalOpen}
          onClose={() => setFormModalOpen(false)}
          onSuccess={handleSiteVisitUpdated}
          onSave={handleCreateSiteVisit}
          agents={agents}
          leads={leads}
          projects={projects}
          statusOptions={statusOptions}
          transportationOptions={transportationOptions}
          visitTypeOptions={visitTypeOptions}
          fetchConfigurationSelect={fetchConfigurationSelect}
          fetchUnitSelect={fetchUnitSelect}
        />
      </Box>
    </>
  );
};

export default SiteVisitBase; 